import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Shield, Users, Star, Gift, LogOut, Settings, BarChart3, ImageIcon, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AdminNav from '../components/AdminNav';

const AdminPanelPage = () => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recovering, setRecovering] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    checkAdminAuth();
  }, []);

  const checkAdminAuth = () => {
    const adminToken = localStorage.getItem('admin_token');
    const adminUser = localStorage.getItem('admin_user');
    
    if (!adminToken || !adminUser) {
      navigate('/admin/login');
      return;
    }
    
    try {
      const userData = JSON.parse(adminUser);
      setAdmin(userData);
    } catch (error) {
      console.error('Error parsing admin user data:', error);
      navigate('/admin/login');
      return;
    }
    
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    
    toast({
      title: 'Logged Out',
      description: 'You have been logged out successfully',
    });
    
    navigate('/admin/login');
  };

  const handleRecoverImages = async () => {
    setRecovering(true);
    const adminToken = localStorage.getItem('admin_token');
    const headers = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };
    try {
      // 1. Re-host bundled reward images and repoint reward_items + purchases
      const rehostRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/rewards/rehost-images`, {
        method: 'POST', headers,
      });
      if (!rehostRes.ok) throw new Error('Re-host step failed');
      const rehost = await rehostRes.json();

      // 2. Sync any remaining purchase image URLs from reward_items
      const syncRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/sync-purchase-images`, {
        method: 'POST', headers,
      });
      const sync = syncRes.ok ? await syncRes.json() : { purchases_updated: 0 };

      const unmatchedCount = (rehost.unmatched_reward_items || []).length;
      toast({
        title: 'Reward Images Recovered',
        description: `Restored ${rehost.reward_items_updated} rewards and ${(rehost.reward_purchases_updated || 0) + (sync.purchases_updated || 0)} collection items.` +
          (unmatchedCount ? ` ${unmatchedCount} reward(s) have no saved image and must be re-uploaded.` : ''),
      });
    } catch (error) {
      toast({
        title: 'Recovery Failed',
        description: error.message || 'Could not recover reward images.',
        variant: 'destructive',
      });
    } finally {
      setRecovering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-xl text-slate-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Navigation */}
      <AdminNav />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            Welcome back, {admin.username}!
          </h2>
          <p className="text-slate-600">
            Manage your homeschool tracking application from this admin panel.
          </p>
        </div>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/admin/users')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                User Management
              </CardTitle>
              <CardDescription>
                View and manage parent and student accounts, subscriptions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-blue-600 hover:bg-blue-700">
                Manage Users
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/admin/rewards/bulk-upload')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-green-600" />
                Bulk Upload Rewards
              </CardTitle>
              <CardDescription>
                Upload multiple rewards at once with spreadsheet editor
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-green-600 hover:bg-green-700">
                Bulk Upload
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/admin/rewards/manage')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-blue-600" />
                Reward Manager
              </CardTitle>
              <CardDescription>
                View and edit individual rewards
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full">
                Manage Rewards
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/admin/rewards')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-slate-600" />
                Reward Settings
              </CardTitle>
              <CardDescription>
                Configure existing reward types and categories
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full">
                Configure Rewards
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/admin/clear-cache')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-orange-600" />
                System Tools
              </CardTitle>
              <CardDescription>
                Clear cache and manage system data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full">
                System Tools
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-purple-600" />
                Recover Reward Images
              </CardTitle>
              <CardDescription>
                Restore broken pet & sticker images and re-link them to student collections
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                className="w-full bg-purple-600 hover:bg-purple-700"
                onClick={handleRecoverImages}
                disabled={recovering}
                data-testid="recover-reward-images-button"
              >
                {recovering ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recovering...</>
                ) : (
                  'Recover Images'
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Admin Status</CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">Active</div>
              <Badge variant="default" className="mt-2">Online</Badge>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">System</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">Operational</div>
              <Badge variant="default" className="mt-2 bg-green-600">Healthy</Badge>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Last Login</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">Now</div>
              <p className="text-xs text-muted-foreground mt-2">
                Current session
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Access Level</CardTitle>
              <Star className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">Full</div>
              <Badge variant="secondary" className="mt-2">Administrator</Badge>
            </CardContent>
          </Card>
        </div>

        {/* Instructions */}
        <Card>
          <CardHeader>
            <CardTitle>Getting Started</CardTitle>
            <CardDescription>
              Use the tools above to manage your homeschool tracking application
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Gift className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold">Reward Manager</h4>
                  <p className="text-sm text-slate-600">
                    Create new stickers and pets, set their costs, availability dates, and upload custom images.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Settings className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold">Reward Settings</h4>
                  <p className="text-sm text-slate-600">
                    Modify existing rewards, change their categories, and bulk update settings.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Settings className="w-5 h-5 text-orange-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold">System Tools</h4>
                  <p className="text-sm text-slate-600">
                    Clear cached data, reset system settings, and perform maintenance tasks.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPanelPage;